/**
 * Desk gate (OD-65): install guide (iPhone Safari) → pairing (one time per device) → silent device
 * sign-in on every start → workspace. The agent never types a password or code after pairing.
 */
import { HopexEmblem } from '@hopex/ui';
import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, signInWithDevice } from './lib/api.ts';
import { loadDevice } from './lib/device.ts';
import { isIos, isStandalone, refreshPush } from './lib/platform.ts';
import type { Me } from './lib/types.ts';
import { InstallGuide } from './screens/Install.tsx';
import { PairScreen } from './screens/Pair.tsx';
import { UpdateBanner } from './screens/Update.tsx';
import { Workspace } from './screens/Workspace.tsx';

type Gate =
  | { s: 'boot' }
  | { s: 'install' }
  | { s: 'pair'; notice?: string | undefined }
  | { s: 'offline'; message: string }
  | { s: 'ready'; me: Me };

const SKIP_INSTALL = 'dk-skip-install';

export function App() {
  const [gate, setGate] = useState<Gate>({ s: 'boot' });

  const boot = useCallback(async () => {
    setGate({ s: 'boot' });
    try {
      const cur = await api.raw<{ me: Me | null }>('GET', '/auth/session');
      if (cur.me) {
        setGate({ s: 'ready', me: cur.me });
        void refreshPush();
        return;
      }
      const device = await loadDevice();
      if (!device) {
        let skip = false;
        try {
          skip = window.sessionStorage.getItem(SKIP_INSTALL) === '1';
        } catch {
          skip = false;
        }
        setGate(isIos() && !isStandalone() && !skip ? { s: 'install' } : { s: 'pair' });
        return;
      }
      const me = await signInWithDevice();
      if (me) {
        setGate({ s: 'ready', me });
        void refreshPush();
      } else setGate({ s: 'pair' });
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'DESK_DEVICE_INVALID')
        setGate({ s: 'pair', notice: e.message });
      else
        setGate({
          s: 'offline',
          message: e instanceof ApiFailure ? e.message : 'ارتباط با سرور برقرار نشد.',
        });
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => void boot(), 0);
    return () => window.clearTimeout(t);
  }, [boot]);

  if (gate.s === 'boot') return <Splash />;
  if (gate.s === 'install')
    return (
      <InstallGuide
        onContinue={() => {
          try {
            window.sessionStorage.setItem(SKIP_INSTALL, '1');
          } catch {
            /* UI preference only */
          }
          setGate({ s: 'pair' });
        }}
      />
    );
  if (gate.s === 'pair')
    return <PairScreen notice={gate.notice} onPaired={(me) => setGate({ s: 'ready', me })} />;
  if (gate.s === 'offline')
    return (
      <Splash>
        <p
          className="m-0 max-w-[300px] text-center text-[13.5px] leading-7 text-fg-muted"
          role="alert"
        >
          {gate.message}
        </p>
        <button
          type="button"
          onClick={() => void boot()}
          className="hx-press mt-2 min-h-11 cursor-pointer rounded-full bg-accent px-6 text-[14px] font-bold text-on-accent shadow-cta"
        >
          تلاش دوباره
        </button>
      </Splash>
    );
  return (
    <>
      <Workspace me={gate.me} onSignedOut={() => setGate({ s: 'pair' })} />
      <UpdateBanner />
    </>
  );
}

export function Splash({ children }: { children?: React.ReactNode }) {
  return (
    <div
      className="hx-ambient grid min-h-dvh place-items-center bg-canvas px-6 text-fg"
      data-desk-splash
    >
      <div className="flex flex-col items-center gap-4">
        <span className="relative grid size-20 place-items-center rounded-[28px] bg-canvas-deep shadow-float hx-frame">
          <HopexEmblem size={40} decorative />
          {!children && <span aria-hidden className="dk-shimmer absolute inset-0 rounded-[28px]" />}
        </span>
        <p className="m-0 text-[17px] font-extrabold">میز پشتیبانی هوپکس</p>
        {children ?? (
          <p className="m-0 text-[12.5px] text-fg-muted" role="status">
            در حال اتصال امن…
          </p>
        )}
      </div>
    </div>
  );
}
